import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import type { QuestionGroupMode, QuestionGroupTemplateContract } from '@/contracts/question-groups'
import { listQuestionGroupTemplates } from '@/lib/data-client/question-groups'
import { cn } from '@/lib/utils'
import { useFormBuilderStore } from '@/stores/formBuilderStore'
import { GitBranch, Layers, Link2, Search } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

type AddQuestionGroupDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const modeOptions: Array<{
  value: QuestionGroupMode
  title: string
  description: string
}> = [
  {
    value: 'synced',
    title: '同步題組',
    description: '問卷引用題組庫內容，題目需回到題組管理修改。',
  },
  {
    value: 'copy',
    title: '從題組建立複本',
    description: '建立只屬於此問卷的題組快照，可在問卷內編輯。',
  },
]

export function AddQuestionGroupDialog({ open, onOpenChange }: AddQuestionGroupDialogProps) {
  const addQuestionGroupFromTemplate = useFormBuilderStore(
    (state) => state.addQuestionGroupFromTemplate,
  )
  const [keyword, setKeyword] = useState('')
  const [mode, setMode] = useState<QuestionGroupMode>('synced')
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [templates, setTemplates] = useState<QuestionGroupTemplateContract[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    if (!open) return
    let canceled = false
    setLoading(true)
    setLoadError('')
    listQuestionGroupTemplates()
      .then((nextTemplates) => {
        if (canceled) return
        setTemplates(nextTemplates)
        setSelectedTemplateId((current) =>
          current && nextTemplates.some((template) => template.id === current) ? current : null,
        )
      })
      .catch((error) => {
        if (canceled) return
        console.error('[MiaoForm] list question group templates failed', error)
        setTemplates([])
        setLoadError('目前無法讀取題組庫，請稍後再試。')
      })
      .finally(() => {
        if (!canceled) setLoading(false)
      })
    return () => {
      canceled = true
    }
  }, [open])
  const filteredTemplates = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLocaleLowerCase()
    if (!normalizedKeyword) return templates
    return templates.filter((template) =>
      [template.title, template.description, template.category, template.tags.join(' ')]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase().includes(normalizedKeyword)),
    )
  }, [keyword, templates])
  const selectedTemplate = templates.find((template) => template.id === selectedTemplateId)

  function addGroup() {
    if (!selectedTemplate) return
    addQuestionGroupFromTemplate(selectedTemplate, mode)
    toast.success(mode === 'synced' ? '已加入同步題組' : '已從題組建立複本')
    onOpenChange(false)
    setKeyword('')
    setSelectedTemplateId(null)
    setMode('synced')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(760px,calc(100dvh-48px))] max-w-4xl flex-col overflow-hidden p-0">
        <div className="shrink-0 border-b border-slate-200 p-5">
          <DialogHeader>
            <DialogTitle>新增題組到問卷</DialogTitle>
            <DialogDescription className="sr-only">選擇題組加入目前問卷。</DialogDescription>
          </DialogHeader>
        </div>
        <div className="grid min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)_auto]">
          <div className="space-y-4 border-b border-slate-200 p-5">
            <div className="relative">
              <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜尋題組標題、分類或標籤"
                className="pl-9"
              />
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {modeOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={cn(
                    'rounded-lg border p-4 text-left transition-colors',
                    mode === option.value
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-950 ring-2 ring-emerald-100'
                      : 'border-slate-200 bg-white hover:border-emerald-200 hover:bg-emerald-50/40',
                  )}
                  onClick={() => setMode(option.value)}
                >
                  <div className="flex items-center gap-2 font-semibold">
                    {option.value === 'synced' ? (
                      <Link2 className="size-4" />
                    ) : (
                      <Layers className="size-4" />
                    )}
                    {option.title}
                  </div>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{option.description}</p>
                </button>
              ))}
            </div>
          </div>
          <ScrollArea className="min-h-0" viewportClassName="p-5">
            {loading ? (
              <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
                正在讀取題組庫...
              </div>
            ) : loadError ? (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-6 text-center text-sm text-rose-700">
                {loadError}
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
                找不到符合條件的題組。
              </div>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {filteredTemplates.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    className={cn(
                      'rounded-lg border bg-white p-4 text-left transition-colors',
                      selectedTemplateId === template.id
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-200 hover:border-indigo-200',
                    )}
                    onClick={() => setSelectedTemplateId(template.id)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-slate-950">
                          {template.title}
                        </div>
                        <p className="mt-1 line-clamp-2 text-sm leading-6 text-slate-500">
                          {template.description || '尚未填寫題組描述'}
                        </p>
                      </div>
                      <Badge variant="secondary" className="shrink-0">
                        {template.questions.length} 題
                      </Badge>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Badge variant="outline">{template.category ?? '未分類'}</Badge>
                      {template.tags.slice(0, 3).map((tag) => (
                        <Badge key={tag} variant="outline">
                          #{tag}
                        </Badge>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-slate-200 p-5">
            <div className="flex min-w-0 items-center gap-2 text-sm text-slate-500">
              <GitBranch className="size-4 shrink-0" />
              <span className="truncate">
                {selectedTemplate ? `已選擇「${selectedTemplate.title}」` : '請先選擇要加入的題組'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                取消
              </Button>
              <Button type="button" disabled={!selectedTemplateId} onClick={addGroup}>
                加入問卷
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
